class PDFViewer {
  constructor(containerId, pdfUrl) {
    this.container = document.getElementById(containerId);
    this.pdfUrl = pdfUrl;
    this.pdfDoc = null;
    this.currentPage = 1;
    this.totalPages = 0;
    this.scale = window.devicePixelRatio || 2; // Utiliser le pixel ratio de l'écran
    this.pagesPerView = 1; // Toujours 1 page à la fois

    this.init();
  }

  async init() {
    try {
      // Afficher le loader
      this.showLoading();

      // Charger PDF.js depuis CDN
      if (typeof pdfjsLib === 'undefined') {
        await this.loadPDFJS();
      }

      // Configurer le worker
      pdfjsLib.GlobalWorkerOptions.workerSrc = 'https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.worker.min.js';

      // Charger le document PDF
      const loadingTask = pdfjsLib.getDocument(this.pdfUrl);
      this.pdfDoc = await loadingTask.promise;
      this.totalPages = this.pdfDoc.numPages;

      // Calculer combien de pages on peut afficher
      await this.calculatePagesPerView();

      // Construire le visualiseur
      await this.buildViewer();

    } catch (error) {
      console.error('Erreur lors du chargement du PDF:', error);
      this.showError('Impossible de charger le rapport PDF.');
    }
  }

  async loadPDFJS() {
    return new Promise((resolve, reject) => {
      const script = document.createElement('script');
      script.src = 'https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.min.js';
      script.onload = resolve;
      script.onerror = reject;
      document.head.appendChild(script);
    });
  }

  async calculatePagesPerView() {
    try {
      // Obtenir la première page pour connaître les dimensions
      const page = await this.pdfDoc.getPage(1);
      const baseViewport = page.getViewport({ scale: 1 });

      // Calculer la hauteur max disponible
      const headerHeight = getComputedStyle(document.documentElement).getPropertyValue('--header-height') || '0px';
      const maxHeight = window.innerHeight - parseFloat(headerHeight) - 150;

      // Calculer le scale pour utiliser toute la hauteur disponible
      const scaleToFitHeight = maxHeight / baseViewport.height;

      // Calculer la largeur qu'une page prendrait avec ce scale
      const pageWidth = baseViewport.width * scaleToFitHeight;

      // Obtenir la largeur disponible du container
      const containerWidth = this.container.clientWidth;

      // Vérifier si 2 pages peuvent rentrer
      // Prendre en compte: gap entre les pages (16px) + padding du groupe (2rem = 32px)
      const gapWidth = 16;
      const groupPadding = 32; // 1rem de chaque côté
      const twoPagesTotalWidth = (pageWidth * 2) + gapWidth + groupPadding;

      // Si 2 pages rentrent, afficher 2 pages
      if (twoPagesTotalWidth <= containerWidth) {
        this.pagesPerView = 2;
      } else {
        this.pagesPerView = 1;
      }

    } catch (error) {
      console.error('Erreur lors du calcul des pages par vue:', error);
      this.pagesPerView = 1; // Fallback à 1 page
    }
  }

  showLoading() {
    this.container.innerHTML = `
      <div class="pdf-viewer">
        <div class="pdf-loading">
          <div class="pdf-spinner"></div>
          <span>Chargement du rapport...</span>
        </div>
      </div>
    `;
  }

  showError(message) {
    this.container.innerHTML = `
      <div class="pdf-viewer">
        <div class="pdf-loading">
          <span style="color: #d32f2f;">${message}</span>
        </div>
      </div>
    `;
  }

  async buildViewer() {
    // Créer la structure HTML
    this.container.innerHTML = `
      <div class="pdf-viewer">
        <div class="pdf-scroll-wrapper" id="pdf-scroll-wrapper" data-pages-per-view="${this.pagesPerView}"></div>
        <div class="pdf-nav">
          <button class="pdf-nav-btn" id="prev-btn">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <polyline points="15 18 9 12 15 6"></polyline>
            </svg>
            Précédent
          </button>
          <div class="pdf-page-info">
            <span id="current-page-display">1</span> / <span id="total-pages-display">${this.totalPages}</span>
          </div>
          <button class="pdf-nav-btn" id="next-btn">
            Suivant
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <polyline points="9 18 15 12 9 6"></polyline>
            </svg>
          </button>
        </div>
      </div>
    `;

    const scrollWrapper = document.getElementById('pdf-scroll-wrapper');

    // Rendre les pages par groupes selon pagesPerView
    if (this.pagesPerView === 2) {
      // Créer des groupes de 2 pages
      for (let pageNum = 1; pageNum <= this.totalPages; pageNum += 2) {
        const groupWrapper = document.createElement('div');
        groupWrapper.className = 'pdf-page-group';
        groupWrapper.dataset.pageNum = pageNum;

        // Première page du groupe
        const pageWrapper1 = document.createElement('div');
        pageWrapper1.className = 'pdf-page-wrapper';
        const canvas1 = document.createElement('canvas');
        canvas1.className = 'pdf-page-canvas';
        pageWrapper1.appendChild(canvas1);
        groupWrapper.appendChild(pageWrapper1);
        await this.renderPage(pageNum, canvas1);

        // Deuxième page du groupe (si elle existe)
        if (pageNum + 1 <= this.totalPages) {
          const pageWrapper2 = document.createElement('div');
          pageWrapper2.className = 'pdf-page-wrapper';
          const canvas2 = document.createElement('canvas');
          canvas2.className = 'pdf-page-canvas';
          pageWrapper2.appendChild(canvas2);
          groupWrapper.appendChild(pageWrapper2);
          await this.renderPage(pageNum + 1, canvas2);
        }

        scrollWrapper.appendChild(groupWrapper);
      }
    } else {
      // Mode 1 page
      for (let pageNum = 1; pageNum <= this.totalPages; pageNum++) {
        const pageWrapper = document.createElement('div');
        pageWrapper.className = 'pdf-page-wrapper';
        pageWrapper.dataset.pageNum = pageNum;

        const canvas = document.createElement('canvas');
        canvas.className = 'pdf-page-canvas';
        pageWrapper.appendChild(canvas);

        scrollWrapper.appendChild(pageWrapper);

        // Rendre la page
        await this.renderPage(pageNum, canvas);
      }
    }

    // Configurer la navigation
    this.setupNavigation();

    // Gérer le scroll pour mettre à jour le numéro de page
    this.setupScrollTracking();

    // Activer le scroll horizontal avec la molette
    this.setupMouseWheelScroll();

    // Gérer le redimensionnement
    window.addEventListener('resize', () => this.handleResize());
  }

  async renderPage(pageNum, canvas) {
    try {
      const page = await this.pdfDoc.getPage(pageNum);

      // Obtenir le viewport de base
      let baseViewport = page.getViewport({ scale: 1 });

      // Calculer la hauteur max disponible du container
      const headerHeight = getComputedStyle(document.documentElement).getPropertyValue('--header-height') || '0px';
      const maxHeight = window.innerHeight - parseFloat(headerHeight) - 150; // 150px pour la navigation

      // Calculer le scale pour utiliser toute la hauteur disponible
      const scaleToFitHeight = maxHeight / baseViewport.height;

      // Utiliser un multiplicateur élevé pour garantir la netteté sur tous les écrans
      // On force un minimum de 3x pour les écrans non-Retina
      const deviceRatio = Math.max(window.devicePixelRatio || 2, 3);
      const renderScale = scaleToFitHeight * deviceRatio;
      const viewport = page.getViewport({ scale: renderScale });

      const context = canvas.getContext('2d');
      canvas.height = viewport.height;
      canvas.width = viewport.width;

      // Pas de taille CSS inline, on laisse le CSS gérer avec height: 100%
      // Le canvas haute résolution sera automatiquement scalé

      const renderContext = {
        canvasContext: context,
        viewport: viewport
      };

      await page.render(renderContext).promise;
    } catch (error) {
      console.error(`Erreur lors du rendu de la page ${pageNum}:`, error);
    }
  }

  setupNavigation() {
    const prevBtn = document.getElementById('prev-btn');
    const nextBtn = document.getElementById('next-btn');
    const scrollWrapper = document.getElementById('pdf-scroll-wrapper');

    prevBtn.addEventListener('click', () => {
      const targetPage = Math.max(1, this.currentPage - this.pagesPerView);
      let targetElement;

      if (this.pagesPerView === 2) {
        targetElement = scrollWrapper.querySelector(`.pdf-page-group[data-page-num="${targetPage}"]`);
      } else {
        targetElement = scrollWrapper.querySelector(`.pdf-page-wrapper[data-page-num="${targetPage}"]`);
      }

      if (targetElement) {
        const scrollLeft = targetElement.offsetLeft - scrollWrapper.offsetLeft;
        scrollWrapper.scrollTo({
          left: scrollLeft,
          behavior: 'smooth'
        });
      }

      this.currentPage = targetPage;
      this.updateNavigationButtons();
    });

    nextBtn.addEventListener('click', () => {
      const targetPage = Math.min(this.totalPages, this.currentPage + this.pagesPerView);
      let targetElement;

      if (this.pagesPerView === 2) {
        targetElement = scrollWrapper.querySelector(`.pdf-page-group[data-page-num="${targetPage}"]`);
      } else {
        targetElement = scrollWrapper.querySelector(`.pdf-page-wrapper[data-page-num="${targetPage}"]`);
      }

      if (targetElement) {
        const scrollLeft = targetElement.offsetLeft - scrollWrapper.offsetLeft;
        scrollWrapper.scrollTo({
          left: scrollLeft,
          behavior: 'smooth'
        });
      }

      this.currentPage = targetPage;
      this.updateNavigationButtons();
    });

    this.updateNavigationButtons();
  }

  setupScrollTracking() {
    const scrollWrapper = document.getElementById('pdf-scroll-wrapper');

    let scrollTimeout;
    scrollWrapper.addEventListener('scroll', () => {
      clearTimeout(scrollTimeout);
      scrollTimeout = setTimeout(() => {
        this.updateCurrentPageFromScroll();
      }, 100);
    });
  }

  setupMouseWheelScroll() {
    const scrollWrapper = document.getElementById('pdf-scroll-wrapper');

    scrollWrapper.addEventListener('wheel', (e) => {
      // Si on scroll horizontalement (Shift + molette), laisser le comportement par défaut
      if (Math.abs(e.deltaX) > 0) {
        return;
      }

      // Vérifier s'il y a du contenu à scroller horizontalement
      const hasHorizontalScroll = scrollWrapper.scrollWidth > scrollWrapper.clientWidth;

      if (!hasHorizontalScroll) {
        // Pas de scroll horizontal possible, laisser le scroll vertical
        return;
      }

      // Si on est au début ou à la fin, permettre le scroll vertical de la page
      const isAtStart = scrollWrapper.scrollLeft <= 0;
      const isAtEnd = scrollWrapper.scrollLeft >= scrollWrapper.scrollWidth - scrollWrapper.clientWidth - 1;

      if ((isAtStart && e.deltaY < 0) || (isAtEnd && e.deltaY > 0)) {
        // Laisser le scroll vertical normal de la page
        return;
      }

      // Sinon, convertir le scroll vertical en horizontal seulement si on scroll suffisamment
      if (Math.abs(e.deltaY) > 2) {
        e.preventDefault();
        scrollWrapper.scrollLeft += e.deltaY;
      }
    }, { passive: false });
  }

  updateCurrentPageFromScroll() {
    const scrollWrapper = document.getElementById('pdf-scroll-wrapper');
    let visiblePage = 1;
    const scrollLeft = scrollWrapper.scrollLeft;
    const wrapperWidth = scrollWrapper.clientWidth;

    if (this.pagesPerView === 2) {
      const groups = scrollWrapper.querySelectorAll('.pdf-page-group');
      groups.forEach((group) => {
        const groupLeft = group.offsetLeft - scrollWrapper.offsetLeft;
        if (scrollLeft >= groupLeft - wrapperWidth / 4) {
          visiblePage = parseInt(group.dataset.pageNum);
        }
      });
    } else {
      const pages = scrollWrapper.querySelectorAll('.pdf-page-wrapper');
      pages.forEach((page, index) => {
        const pageLeft = page.offsetLeft - scrollWrapper.offsetLeft;
        if (scrollLeft >= pageLeft - wrapperWidth / 4) {
          visiblePage = index + 1;
        }
      });
    }

    this.currentPage = visiblePage;
    this.updateNavigationButtons();
  }

  updateNavigationButtons() {
    const prevBtn = document.getElementById('prev-btn');
    const nextBtn = document.getElementById('next-btn');
    const currentPageDisplay = document.getElementById('current-page-display');

    if (prevBtn && nextBtn && currentPageDisplay) {
      prevBtn.disabled = this.currentPage <= 1;
      nextBtn.disabled = this.currentPage >= this.totalPages - this.pagesPerView + 1;
      currentPageDisplay.textContent = this.currentPage;
    }
  }

  handleResize() {
    // Plus besoin de reconstruire le viewer, 1 page partout
  }
}

// Fonction globale pour initialiser le visualiseur
window.initPDFViewer = function(containerId, pdfUrl) {
  new PDFViewer(containerId, pdfUrl);
};
